// Where parts touch. Two faces touch when their planes coincide with opposite normals and
// their outlines overlap; two boards whose volumes overlap cross each other (lap joint).
// Every contact is classified by which faces meet — that decides which joints fit:
//
//   T         end of one part on the face/edge of another, somewhere along it
//   L         the same at a corner: the other part's end is flush with this part's side
//   kante     the long edge of a part on a face (back panel, bottom in a groove)
//   leimfuge  edge on edge (glued-up panel, bevelled shade segments)
//   flaeche   face on face
//   stoss     end on end (mitred frame segments, lengthening)
//   rund      end of a round rod on a face
//   kreuz     two boards overlapping (half lap)
//   montage   anything with a bought part

import {
  Vector3, partMatrix, cutPlanes, hexaCorners, FACE_QUADS, sizeOf, isRound, planeBasis, clipConvex, polyArea,
  circlePoly, axesOf, aabbOf,
} from './geometry.js'

export const TOL = 0.6 // mm: planes this close count as touching
const PAR = 0.9995 // |cos| for "parallel"

export const KIND_LABEL = {
  T: 'T-Stoß', L: 'Eckverbindung', kante: 'Kante auf Fläche', leimfuge: 'Kante an Kante', flaeche: 'Fläche auf Fläche',
  stoss: 'Stirn an Stirn', rund: 'Rundstab', kreuz: 'Kreuzung', ring: 'Ring durch Lamelle', montage: 'Kaufteil',
}

/** Everything the contact search and the joints need about one part. */
export function shapeOf(part) {
  const m = partMatrix(part)
  const size = sizeOf(part)
  const planes = cutPlanes(part)
  const corners = hexaCorners(planes)
  const cornersW = corners.map((p) => p.clone().applyMatrix4(m))
  const axes = axesOf(m)
  const pos = new Vector3().setFromMatrixPosition(m)
  const faces = []
  const hw = part.type === 'hw'
  if (part.type === 'profile') {
    // Flat faces: the outline's convex hull (an approximation for C-shapes); every outline
    // segment is a narrow edge face, so straight joins (two half rings) meet face to face.
    const z = part.T / 2
    const hull2 = hull2d(part.outline)
    for (const [f, sz] of [[4, -1], [5, 1]]) {
      const n = axes[2].clone().multiplyScalar(sz)
      const poly = (sz > 0 ? hull2 : [...hull2].reverse()).map(([x, y]) => new Vector3(x, y, sz * z).applyMatrix4(m))
      faces.push({ f, role: 'face', n, d: n.dot(poly[0]), poly })
    }
    const o = part.outline
    const ccw = area2d(o) > 0
    for (let i = 0; i < o.length; i++) {
      const a = o[i], b = o[(i + 1) % o.length]
      const ex = b[0] - a[0], ey = b[1] - a[1], len = Math.hypot(ex, ey)
      if (len < 1) continue
      // Outward normal of the segment (right of the direction for a CCW outline).
      const nl = ccw ? new Vector3(ey / len, -ex / len, 0) : new Vector3(-ey / len, ex / len, 0)
      const n = nl.transformDirection(m)
      const poly = [[a, -z], [b, -z], [b, z], [a, z]].map(([p, zz]) => new Vector3(p[0], p[1], zz).applyMatrix4(m))
      faces.push({ f: 2 + (i % 2), seg: i, role: 'edge', n, d: n.dot(poly[0]), poly })
    }
  } else if (isRound(part)) {
    const [L, D] = size
    const role = hw ? 'hw' : L >= D * 0.8 ? 'end' : 'face'
    for (const f of [0, 1]) {
      const n = axes[0].clone().multiplyScalar(f ? 1 : -1)
      const c = pos.clone().addScaledVector(n, L / 2)
      faces.push({ f, role, n, d: n.dot(c), poly: circlePoly(c, n, D / 2) })
    }
  } else if (!(hw && part.hw === 'birne')) {
    const [, W, T] = size
    for (let f = 0; f < 6; f++) {
      const role = hw ? 'hw' : f < 2 ? 'end' : (f < 4) === (W >= T) ? 'edge' : 'face'
      const pl = planes[f]
      const n = pl.n.clone().transformDirection(m)
      const poly = FACE_QUADS[f].map((c) => cornersW[c])
      faces.push({ f, role, n, d: n.dot(poly[0]), poly })
    }
  }
  return { part, id: part.id, m, size, planes, corners, cornersW, axes, pos, faces, box: aabbOf(cornersW), round: isRound(part), hw }
}

function area2d(p) { let s = 0; for (let i = 0; i < p.length; i++) { const a = p[i], b = p[(i + 1) % p.length]; s += a[0] * b[1] - b[0] * a[1] } return s / 2 }
function hull2d(pts) {
  const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lo = [], up = []
  for (const q of p) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q) }
  for (const q of p.reverse()) { while (up.length >= 2 && cross(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q) }
  return [...lo.slice(0, -1), ...up.slice(0, -1)]
}

function overlapPoly(fa, fb) {
  const [u, v] = planeBasis(fa.n)
  const to2 = (p) => [p.dot(u), p.dot(v)]
  const poly = clipConvex(fa.poly.map(to2), fb.poly.map(to2))
  if (poly.length < 3) return null
  const area = polyArea(poly)
  // Back to 3D on face a's plane.
  const pts = poly.map(([x, y]) => u.clone().multiplyScalar(x).addScaledVector(v, y).addScaledVector(fa.n, fa.d))
  return { area, pts }
}

const boxesNear = (a, b, tol) =>
  a.box.min.x <= b.box.max.x + tol && b.box.min.x <= a.box.max.x + tol &&
  a.box.min.y <= b.box.max.y + tol && b.box.min.y <= a.box.max.y + tol &&
  a.box.min.z <= b.box.max.z + tol && b.box.min.z <= a.box.max.z + tol

/** Female end face coplanar with a side face of the male → the joint sits at a corner. */
function cornerOf(male, female) {
  for (const fe of female.faces) {
    if (fe.role !== 'end') continue
    for (const ms of male.faces) {
      if (ms.f < 2) continue
      if (fe.n.dot(ms.n) > PAR && Math.abs(fe.d - ms.d) < TOL) return { fe: fe.f, ms: ms.f, e: fe.n.clone() }
    }
  }
  return null
}

function classify(sa, sb, fa, fb) {
  const ra = fa.role, rb = fb.role
  const pick = (male, mf, female, ff, kind) => ({ male, mf, female, ff, kind })
  if (ra === 'hw' || rb === 'hw') return ra === 'hw' ? pick(sa, fa, sb, fb, 'montage') : pick(sb, fb, sa, fa, 'montage')
  if (ra === 'end' && rb === 'end') return pick(sb, fb, sa, fa, 'stoss')
  if (ra === 'end' || rb === 'end') {
    const [m, mf, f, ff] = ra === 'end' ? [sa, fa, sb, fb] : [sb, fb, sa, fa]
    if (m.round) return pick(m, mf, f, ff, 'rund')
    return pick(m, mf, f, ff, 'T')
  }
  if (ra === 'edge' && rb === 'edge') return pick(sb, fb, sa, fa, 'leimfuge')
  if (ra === 'edge' || rb === 'edge') return ra === 'edge' ? pick(sa, fa, sb, fb, 'kante') : pick(sb, fb, sa, fa, 'kante')
  const smaller = polyArea2(fa) <= polyArea2(fb)
  return smaller ? pick(sa, fa, sb, fb, 'flaeche') : pick(sb, fb, sa, fa, 'flaeche')
}
const polyArea2 = (face) => {
  const [u, v] = planeBasis(face.n)
  return polyArea(face.poly.map((p) => [p.dot(u), p.dot(v)]))
}

/**
 * The frame every joint is built in: origin on the shared plane, n = out of the female
 * (toward the male), u across the male's thickness (at a corner: toward the outside),
 * v = n × u along the joint. Ranges are the contact's extent in u and v.
 */
function frameOf(c, male, female) {
  const n = c.n
  let u
  if (c.corner) u = c.corner.e.clone()
  else {
    const cand = male.round ? female.axes : [male.axes[2], male.axes[1], male.axes[0]]
    u = cand.map((a) => a.clone().addScaledVector(n, -a.dot(n))).find((a) => a.lengthSq() > 0.25) || planeBasis(n)[0]
    u.normalize()
  }
  const v = new Vector3().crossVectors(n, u).normalize()
  const o = c.pts.reduce((s, p) => s.add(p), new Vector3()).multiplyScalar(1 / c.pts.length)
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity
  for (const p of c.pts) {
    const d = p.clone().sub(o)
    u0 = Math.min(u0, d.dot(u)); u1 = Math.max(u1, d.dot(u))
    v0 = Math.min(v0, d.dot(v)); v1 = Math.max(v1, d.dot(v))
  }
  // Re-centre the origin on the contact rectangle.
  o.addScaledVector(u, (u0 + u1) / 2).addScaledVector(v, (v0 + v1) / 2)
  const hu = (u1 - u0) / 2, hv = (v1 - v0) / 2
  const dF = n.dot(o)
  const depthF = Math.max(...female.cornersW.map((p) => dF - n.dot(p)))
  const depthM = Math.max(...male.cornersW.map((p) => n.dot(p) - dF))
  return { o, n, u, v, hu, hv, depthF, depthM }
}

/** Overlapping boards whose axes are parallel: a crossing (lap joint). */
function crossing(sa, sb) {
  if (sa.round || sb.round || sa.hw || sb.hw || sa.part.type === 'profile' || sb.part.type === 'profile') return null
  for (const ax of sb.axes) if (!sa.axes.some((a) => Math.abs(a.dot(ax)) > PAR)) return null
  const inv = sa.m.clone().invert()
  const lb = aabbOf(sb.cornersW.map((p) => p.clone().applyMatrix4(inv)))
  const la = aabbOf(sa.corners)
  const lo = la.min.clone().max(lb.min), hi = la.max.clone().min(lb.max)
  const ov = hi.clone().sub(lo)
  if (ov.x <= 0.5 || ov.y <= 0.5 || ov.z <= 0.5) return null
  // Stack axis: where both parts are fully overlapped (the thickness both share).
  const full = [0, 1, 2].filter((k) => Math.abs(ov.getComponent(k) - la.size.getComponent(k)) < 0.6 && Math.abs(ov.getComponent(k) - lb.size.getComponent(k)) < 0.6)
  const cands = full.length ? full : [0, 1, 2]
  const k = cands.reduce((best, i) => (ov.getComponent(i) < ov.getComponent(best) ? i : best), cands[0])
  return { lo, hi, k, la: { min: la.min, max: la.max }, lb: { min: lb.min, max: lb.max } }
}

/**
 * A radial board (slat) passing through a ring or disc: the ring's plane cuts through the
 * board and their radial ranges overlap. The ring is a rod-type disc, its central through
 * hole (if any) gives the inner radius. Returns the overlap in the ring's frame.
 */
/** Ring geometry of a part that slats can pass through: a rod disc, or a profile ring. */
function ringOf(s) {
  if (s.hw) return null
  if (s.round) {
    const hole = (s.part.holes || []).find((h) => (h.face === 'x+' || h.face === 'x-') && !h.depth && Math.abs(h.a || 0) < 0.5 && Math.abs(h.b || 0) < 0.5)
    return { up: s.axes[0].clone(), C: s.pos.clone(), halfL: s.size[0] / 2, ro: s.size[1] / 2, ri: hole ? hole.d / 2 : 0, span: null }
  }
  const r = s.part.type === 'profile' && s.part.ring
  if (!r) return null
  return { up: s.axes[2].clone(), C: new Vector3(r.cx || 0, r.cy || 0, 0).applyMatrix4(s.m), halfL: s.part.T / 2, ro: r.ro, ri: r.ri || 0, span: [r.a0, r.a1], xa: s.axes[0], ya: s.axes[1] }
}

function crossingRound(sa, sb) {
  const isBoard = (s) => !s.round && !s.hw && s.part.type !== 'profile'
  const ra = ringOf(sa), rb = ringOf(sb)
  const [rod, board, R] = ra && isBoard(sb) ? [sa, sb, ra] : rb && isBoard(sa) ? [sb, sa, rb] : [null, null, null]
  if (!rod) return null
  const { up, C, halfL, ro, ri } = R
  const rel = board.pos.clone().sub(C)
  rel.addScaledVector(up, -rel.dot(up))
  if (rel.length() < 1) return null
  // The slat's width direction in the ring plane (radial, or slanted like a turbine blade),
  // pointing outward; the slat slides onto the ring along it.
  const inPlane = board.axes.map((a, i) => ({ a: a.clone().addScaledVector(up, -a.dot(up)), size: board.size[i] }))
    .filter((x) => x.a.lengthSq() > 0.9).sort((p, q) => q.size - p.size)
  if (inPlane.length < 2) return null
  const w = inPlane[0].a.normalize()
  if (w.dot(rel) < 0) w.negate()
  if (w.dot(rel.clone().normalize()) < 0.2) return null // nearly tangential: not a slat on a ring
  const tang = new Vector3().crossVectors(up, w)
  const span = (ax) => { const ps = board.cornersW.map((p) => p.clone().sub(C).dot(ax)); return [Math.min(...ps), Math.max(...ps)] }
  const [u0, u1] = span(up), [s0, s1] = span(w), [t0, t1] = span(tang)
  // The board must pass right through the ring's thickness and be thin across.
  if (u0 > -halfL - 0.01 || u1 < halfL + 0.01) return null
  if (t1 - t0 > Math.min(...board.size) * 1.05 + 0.5) return null
  // Along the slat (parameter s from the ring axis), the ring covers radius ri … ro. The
  // outer circle is reached at s = sqrt(ro² − offset²) — take the outermost face line.
  const off = Math.min(Math.abs(t0), Math.abs(t1), t0 < 0 && t1 > 0 ? 0 : Infinity)
  if (off >= ro) return null
  const sRo = Math.sqrt(ro * ro - off * off)
  const offC = Math.abs((t0 + t1) / 2)
  const sRi = ri > offC ? Math.sqrt(ri * ri - offC * offC) : 0
  const lo = Math.max(s0, sRi), hi = Math.min(s1, sRo)
  if (hi - lo < 0.5) return null
  // A ring segment (half ring): the slat must cross it within its angular span.
  if (R.span) {
    const p = C.clone().addScaledVector(w, (lo + hi) / 2).addScaledVector(tang, (t0 + t1) / 2).sub(C)
    let ang = Math.atan2(p.dot(R.ya), p.dot(R.xa))
    const [a0, a1] = R.span
    while (ang < a0 - 1e-6) ang += Math.PI * 2
    if (ang > a1 + 1e-6) return null
  }
  return { round: true, up, radial: w, tang, C, lo, hi, s0, s1, ro: sRo, ri, tc: (t0 + t1) / 2, tw: t1 - t0, halfL, board: board.id, rod: rod.id }
}

export function findContacts(parts) {
  const shapes = new Map(parts.map((p) => [p.id, shapeOf(p)]))
  const list = [...shapes.values()]
  const out = []
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      let sa = list[i], sb = list[j]
      if (sa.id > sb.id) [sa, sb] = [sb, sa]
      if (!boxesNear(sa, sb, 1)) continue
      const xr = crossingRound(sa, sb)
      if (xr) {
        const o = xr.C.clone().addScaledVector(xr.radial, (xr.lo + xr.hi) / 2).addScaledVector(xr.tang, xr.tc)
        out.push({ key: `${sa.id}|${sb.id}|x`, a: sa.id, b: sb.id, kind: 'ring', male: xr.board, female: xr.rod, cross: xr, n: xr.radial.clone(), o, area: 0 })
        continue
      }
      const x = crossing(sa, sb)
      if (x) {
        const n = sa.axes[x.k].clone()
        const mid = x.lo.clone().add(x.hi).multiplyScalar(0.5).applyMatrix4(sa.m)
        out.push({ key: `${sa.id}|${sb.id}|x`, a: sa.id, b: sb.id, kind: 'kreuz', male: sa.id, female: sb.id, cross: x, n, o: mid, area: 0 })
        continue
      }
      for (const fa of sa.faces) {
        for (const fb of sb.faces) {
          if (fa.n.dot(fb.n) > -PAR || Math.abs(fa.d + fb.d) > TOL) continue
          const ov = overlapPoly(fa, fb)
          if (!ov || ov.area < 20) continue
          const cl = classify(sa, sb, fa, fb)
          const c = {
            key: `${sa.id}:${fa.seg ?? fa.f}|${sb.id}:${fb.seg ?? fb.f}`, a: sa.id, b: sb.id, kind: cl.kind,
            male: cl.male.id, female: cl.female.id, mf: cl.mf.f, ff: cl.ff.f,
            n: cl.ff.n.clone(), pts: ov.pts, area: ov.area,
          }
          if (c.kind === 'T') {
            const corner = cornerOf(cl.male, cl.female)
            if (corner) { c.kind = 'L'; c.corner = corner }
          }
          Object.assign(c, frameOf(c, cl.male, cl.female))
          out.push(c)
        }
      }
    }
  }
  return { contacts: out, shapes }
}
